export class FormDynamicList extends HTMLElement {
    static get observedAttributes() {
        return ['label', 'placeholder', 'values', 'with-weights', 'with-limits', 'with-mappings', 'add-text', 'options'];
    }

    private _values: any[] = [];
    private _dragSrcIndex: number | null = null;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            if (name === 'values') {
                try {
                    this._values = JSON.parse(newValue);
                    if (!Array.isArray(this._values)) this._values = [];
                } catch (e) {
                    this._values = [];
                }
                this.render();
            } else if (name === 'with-weights') {
                const hasWeights = newValue !== null;
                this._values = this._values.map(v => {
                    if (hasWeights) {
                        return typeof v === 'object' ? v : { value: v, weight: 1 };
                    } else {
                        return typeof v === 'object' ? v.value : v;
                    }
                });
                this.render();
            } else if (name === 'with-limits') {
                const hasLimits = newValue !== null;
                this._values = this._values.map(v => {
                    if (hasLimits) {
                        return typeof v === 'object' ? v : { value: v, limit: 0 };
                    } else {
                        return typeof v === 'object' ? v.value : v;
                    }
                });
                this.render();
            } else if (name === 'with-mappings') {
                const hasMappings = newValue !== null;
                this._values = this._values.map(v => {
                    if (hasMappings) {
                        return typeof v === 'object' ? v : { value: v, mapping: '' };
                    } else {
                        return typeof v === 'object' ? v.value : v;
                    }
                });
                this.render();
            } else {
                this.render();
            }
        }
    }

    get values() {
        return this._values;
    }

    set values(val: any[]) {
        this._values = val;
        this.render();
    }

    private handleInputChange(index: number, val: string, field: 'value' | 'weight' | 'limit' | 'mapping' = 'value') {
        const withWeights = this.hasAttribute('with-weights');
        const withLimits = this.hasAttribute('with-limits');
        const withMappings = this.hasAttribute('with-mappings');

        if (withWeights || withLimits || withMappings) {
            if (typeof this._values[index] !== 'object') {
                this._values[index] = { value: this._values[index] || '' };
                if (withWeights) this._values[index].weight = 1;
                if (withLimits) this._values[index].limit = 0;
                if (withMappings) this._values[index].mapping = '';
            }
            if (field === 'value') {
                this._values[index].value = val;
            } else if (field === 'weight') {
                let intVal = parseInt(val);
                if (isNaN(intVal) || intVal < 0) intVal = 0;
                this._values[index].weight = intVal;
                // Update specific percentage label if possible without full render
                this.updateDisplayLabels();
            } else if (field === 'limit') {
                let intVal = parseInt(val);
                if (isNaN(intVal) || intVal < 0) intVal = 0;
                this._values[index].limit = intVal;
            } else if (field === 'mapping') {
                this._values[index].mapping = val;
            }
        } else {
            this._values[index] = val;
        }

        this.dispatchChange();
        // Do NOT call this.render() here to avoid breaking cursor/focus/delete behavior
    }

    private updateDisplayLabels() {
        if (!this.shadowRoot) return;
        const withWeights = this.hasAttribute('with-weights');
        if (!withWeights) return;

        const totalWeight = this._values.reduce((sum: number, item: any) => sum + (parseInt(item.weight) || 0), 0);

        // Update Total Shares label
        const totalLabel = this.shadowRoot.querySelector('.total-weight');
        if (totalLabel) {
            totalLabel.textContent = `Total Shares: ${totalWeight}`;
        }

        // Update individual percentages
        this._values.forEach((val, i) => {
            const percentLabel = this.shadowRoot?.querySelector(`.list-item[data-index="${i}"] .weight-percent`);
            if (percentLabel) {
                const weight = parseInt((val as any).weight) || 0;
                const percent = totalWeight > 0 ? Math.round((weight / totalWeight) * 100) + '%' : '0%';
                percentLabel.textContent = percent;
            }
        });
    }

    private handleAdd() {
        const withWeights = this.hasAttribute('with-weights');
        const withLimits = this.hasAttribute('with-limits');
        const withMappings = this.hasAttribute('with-mappings');
        let newItem: any = '';
        if (withWeights) newItem = { value: '', weight: 1 };
        else if (withLimits) newItem = { value: '', limit: 0 };
        else if (withMappings) newItem = { value: '', mapping: '' };

        this._values.push(newItem);
        this.render();
        this.dispatchChange();
    }

    private handleRemove(index: number) {
        this._values.splice(index, 1);
        this.render();
        this.dispatchChange();
    }

    private handleDragStart(e: DragEvent, index: number) {
        this._dragSrcIndex = index;
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', index.toString());
        }
        const item = (e.target as HTMLElement).closest('.list-item');
        if (item) {
            item.classList.add('dragging');
        }
    }

    private handleDragOver(e: DragEvent) {
        if (e.preventDefault) {
            e.preventDefault();
        }
        if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'move';
        }

        const item = (e.target as HTMLElement).closest('.list-item');
        if (item) {
            item.classList.add('over');
        }
        return false;
    }

    private handleDragLeave(e: DragEvent) {
        const item = (e.target as HTMLElement).closest('.list-item');
        if (item) {
            item.classList.remove('over');
        }
    }

    private handleDrop(e: DragEvent, targetIndex: number) {
        e.stopPropagation();
        e.preventDefault();

        this.shadowRoot?.querySelectorAll('.list-item').forEach(el => {
            el.classList.remove('over', 'dragging');
        });

        if (this._dragSrcIndex !== null && this._dragSrcIndex !== targetIndex) {
            const toMove = this._values[this._dragSrcIndex];
            this._values.splice(this._dragSrcIndex, 1);
            this._values.splice(targetIndex, 0, toMove);

            this.render();
            this.dispatchChange();
        }
        this._dragSrcIndex = null;
        return false;
    }

    private dispatchChange() {
        this.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            composed: true,
            detail: { values: this._values }
        }));
    }

    render() {
        const label = this.getAttribute('label') || '';
        const placeholder = this.getAttribute('placeholder') || '';
        const withWeights = this.hasAttribute('with-weights');
        const withLimits = this.hasAttribute('with-limits');
        const withMappings = this.hasAttribute('with-mappings');

        const addText = this.getAttribute('add-text') || 'Add Another Destination';
        let optionsList: string[] = [];
        try {
            const rawOptions = this.getAttribute('options');
            if (rawOptions) optionsList = JSON.parse(rawOptions);
        } catch(e) {}

        let itemsToRender = this._values;
        if (this._values.length === 0) {
            if (withWeights) itemsToRender = [{ value: '', weight: 1 }];
            else if (withLimits) itemsToRender = [{ value: '', limit: 0 }];
            else if (withMappings) itemsToRender = [{ value: '', mapping: '' }];
            else itemsToRender = [''];
        }

        let totalWeight = 0;
        if (withWeights) {
            totalWeight = itemsToRender.reduce((sum: number, item: any) => sum + (parseInt(item.weight) || 0), 0);
        }

        if (this.shadowRoot) {
            // Save focus and cursor position
            const active = this.shadowRoot.activeElement as HTMLInputElement;
            const activeIdx = active?.dataset?.index;
            const activeField = active?.dataset?.field;
            const selectionStart = active?.selectionStart;
            const selectionEnd = active?.selectionEnd;

            this.shadowRoot.innerHTML = `
                <style>
                    :host {
                        display: block;
                        margin-bottom: 1.5rem;
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .form-group {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }
                    label {
                        color: #cbd5e1;
                        font-size: 0.9rem;
                        font-weight: 600;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .total-weight {
                        font-size: 0.8rem;
                        color: #94a3b8;
                        background: rgba(255,255,255,0.05);
                        padding: 2px 6px;
                        border-radius: 4px;
                    }
                    .list-container {
                        display: flex;
                        flex-direction: column;
                        gap: 0.75rem;
                    }
                    .list-item {
                        display: flex;
                        gap: 0.75rem; 
                        align-items: center;
                        background: transparent;
                        border: 1px solid transparent;
                        border-radius: 8px;
                        transition: all 0.2s;
                    }
                    .list-item.dragging {
                        opacity: 0.4;
                    }
                    .list-item.over {
                        border-color: var(--accent, #f59e0b);
                        background: rgba(245, 158, 11, 0.05);
                    }
                    .drag-handle {
                        cursor: grab;
                        color: #64748b;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 0.25rem;
                        opacity: 0.5;
                        transition: all 0.2s;
                    }
                    .drag-handle:hover {
                        opacity: 1;
                        color: white;
                    }
                    input {
                        width: 100%;
                        padding: 0.75rem;
                        background: rgba(255, 255, 255, 0.05);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 8px;
                        color: white;
                        font-size: 0.9rem;
                        font-family: inherit;
                        box-sizing: border-box;
                        transition: all 0.2s;
                    }
                    input:focus {
                        outline: none;
                        border-color: var(--accent, #f59e0b);
                        box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
                    }
                    input.weight-input, input.limit-input, input.mapping-input {
                        width: 80px;
                        text-align: center;
                        flex-shrink: 0;
                    }
                    input.limit-input {
                        width: 100px;
                    }
                    input.mapping-input {
                        width: 120px;
                    }
                    .weight-percent {
                        font-size: 0.8rem;
                        color: #94a3b8;
                        width: 50px;
                        text-align: center;
                        flex-shrink: 0;
                        opacity: 0.8;
                    }
                    .btn-remove {
                        background: rgba(239, 68, 68, 0.1);
                        border: 1px solid rgba(239, 68, 68, 0.2);
                        color: #ef4444;
                        width: 42px;
                        height: 42px;
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        transition: all 0.2s;
                        padding: 0;
                    }
                    .btn-remove svg {
                        width: 20px;
                        height: 20px;
                    }
                    .btn-remove:hover {
                        background: rgba(239, 68, 68, 0.2);
                        border-color: #ef4444;
                    }
                    .btn-add {
                        background: transparent;
                        border: 1px dashed rgba(255, 255, 255, 0.2);
                        color: #94a3b8;
                        padding: 0.75rem;
                        border-radius: 8px;
                        cursor: pointer;
                        width: 100%;
                        margin-top: 0.5rem;
                        font-size: 0.85rem;
                        font-weight: 600;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        transition: all 0.2s;
                    }
                    .btn-add:hover {
                        border-color: var(--accent, #f59e0b);
                        color: white;
                        background: rgba(255, 255, 255, 0.02);
                    }
                </style>
                <div class="form-group">
                    <label>
                        <span>${label}</span>
                        ${withWeights ? `<span class="total-weight">Total Shares: ${totalWeight}</span>` : ''}
                    </label>
                    <datalist id="dynamic-list-options">
                        ${optionsList.map(opt => `<option value="${opt}">`).join('')}
                    </datalist>
                    <div class="list-container">
                        ${itemsToRender.map((val: any, i: number) => {
                const valueOf = (typeof val === 'object') ? (val.value || '') : val;
                const weight = withWeights ? (parseInt(val.weight) || 0) : 1;
                const limit = withLimits ? (parseInt(val.limit) || 0) : 0;
                const mapping = withMappings ? (val.mapping || '') : '';

                let percent = '0%';
                if (withWeights && totalWeight > 0) {
                    percent = Math.round((weight / totalWeight) * 100) + '%';
                }

                return `
                            <div class="list-item" data-index="${i}">
                                <div class="drag-handle" draggable="true" data-index="${i}">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <circle cx="9" cy="5" r="1" />
                                        <circle cx="9" cy="12" r="1" />
                                        <circle cx="9" cy="19" r="1" />
                                        <circle cx="15" cy="5" r="1" />
                                        <circle cx="15" cy="12" r="1" />
                                        <circle cx="15" cy="19" r="1" />
                                    </svg>
                                </div>
                                <input type="text" value="${valueOf}" placeholder="${placeholder}" data-index="${i}" data-field="value" list="dynamic-list-options" style="flex: 1;" autocomplete="off" />
                                ${withWeights ? `
                                    <input type="number" min="0" step="1" value="${weight}" class="weight-input" placeholder="W" data-index="${i}" data-field="weight" />
                                    <span class="weight-percent">${percent}</span>
                                ` : ''}
                                ${withLimits ? `
                                    <input type="number" min="0" step="1" value="${limit}" class="limit-input" placeholder="Limit" data-index="${i}" data-field="limit" />
                                ` : ''}
                                ${withMappings ? `
                                    <input type="text" value="${mapping}" class="mapping-input" placeholder="Match Value" data-index="${i}" data-field="mapping" />
                                ` : ''}
                                <button type="button" class="btn-remove" data-index="${i}" 
                                    style="${i === 0 && itemsToRender.length === 1 ? 'visibility: hidden;' : ''}">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <polyline points="3 6 5 6 21 6"></polyline>
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    </svg>
                                </button>
                            </div>
                        `;
            }).join('')}
                    </div>
                    <button type="button" class="btn-add" id="btnAdd">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        ${addText}
                    </button>
                </div>
            `;

            this.shadowRoot.querySelectorAll('input').forEach(input => {
                input.addEventListener('mousedown', (e) => e.stopPropagation());
                input.addEventListener('input', (e) => {
                    const target = e.target as HTMLInputElement;
                    const idx = parseInt(target.dataset.index || '0');
                    const field = (target.dataset.field || 'value') as any;
                    this.handleInputChange(idx, target.value, field);
                });
            });

            this.shadowRoot.querySelectorAll('.drag-handle').forEach(handle => {
                const idx = parseInt((handle as HTMLElement).dataset.index || '0');
                handle.addEventListener('dragstart', (e) => this.handleDragStart(e as DragEvent, idx));
            });

            this.shadowRoot.querySelectorAll('.list-item').forEach(item => {
                const idx = parseInt((item as HTMLElement).dataset.index || '0');
                item.addEventListener('dragover', (e) => this.handleDragOver(e as DragEvent));
                item.addEventListener('dragleave', (e) => this.handleDragLeave(e as DragEvent));
                item.addEventListener('drop', (e) => this.handleDrop(e as DragEvent, idx));
                item.addEventListener('dragend', (e) => {
                    const listItem = (e.target as HTMLElement).closest('.list-item');
                    if (listItem) listItem.classList.remove('dragging');
                    this.shadowRoot?.querySelectorAll('.over').forEach(el => el.classList.remove('over'));
                });
            });

            this.shadowRoot.querySelectorAll('.btn-remove').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const target = (e.target as HTMLElement).closest('.btn-remove') as HTMLButtonElement;
                    if (target) {
                        const idx = parseInt(target.dataset.index || '0');
                        this.handleRemove(idx);
                    }
                });
            });

            this.shadowRoot.getElementById('btnAdd')?.addEventListener('click', () => {
                this.handleAdd();
            });

            // Restore focus and cursor position
            if (activeIdx !== undefined && activeField !== undefined) {
                const newActive = this.shadowRoot.querySelector(`input[data-index="${activeIdx}"][data-field="${activeField}"]`) as HTMLInputElement;
                if (newActive) {
                    newActive.focus();
                    if (selectionStart !== null && selectionEnd !== null) {
                        newActive.setSelectionRange(selectionStart, selectionEnd);
                    }
                }
            }
        }
    }
}

customElements.define('form-dynamic-list', FormDynamicList);
